import { useEffect } from "react";
import { useLocation, Navigate } from "react-router-dom";
import StripePayment from "./StripePayment";
import { CreatePayment } from "../../../services/subscriptionService";
import useMutation from "../../../hooks/useMutation";
import { paymentSuccess } from "../../../services/PaymentService";
import { useSelector } from "react-redux";

const PaymentPage = ({ selectedPlan: externalPlan, onPaymentSuccess, orderId}) => {
  const { state } = useLocation();
  const activeBookId = useSelector(state => state.stepFlow.activeBookId)
  const selectedPlan = externalPlan || state?.selectedPlan;

  const {
    mutate: createPayment,
    data: stripeData,
    error,
    isPending,
  } = useMutation({
    mutationFn: async ({ planId, bookId }) => {
      const res = await CreatePayment({ planId, bookId });
      if (res?.status === 200) return res.data;
      throw new Error(res?.message || "Failed to create payment.");
    },
  });

  useEffect(() => {
    if (!!selectedPlan?._id && !!activeBookId) {
      createPayment({ planId: selectedPlan._id, bookId: activeBookId });
    }
  }, [selectedPlan, activeBookId]);

  if (!selectedPlan) return <Navigate to="/" replace />;

  const price = selectedPlan.planPricingData?.price?.toFixed(2);
  const currency = selectedPlan.planPricingData?.currency || "USD";
  const features = selectedPlan.features || [];

  return (
    <div className="max-w-6xl mx-auto mt-10 p-4 bg-white rounded-lg shadow-lg grid grid-cols-1 md:grid-cols-2 gap-8">
      {/* Left: Plan Info */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold">{selectedPlan.name}</h2>
        {selectedPlan.description && (
          <p className="text-slate-600 text-base">{selectedPlan.description}</p>
        )}
        <div className="text-3xl font-bold text-md-pink">
          {currency} ${price}
        </div>
        <ul className="list-disc pl-6 text-slate-700 space-y-2">
          {features.map((f) => (
            <li key={f._id} className="text-base">
              {f.name}
            </li>
          ))}
        </ul>
      </div>

      {/* Right: Stripe Form */}
      <div>
        <h3 className="text-xl font-semibold mb-4">Payment</h3>
        {isPending && (
          <div className="flex items-center justify-center gap-3 bg-blue-50 border border-blue-200 text-blue-600 text-sm px-4 py-3 rounded-md shadow-sm mb-4">
            <svg
              className="w-5 h-5 animate-spin text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              />
            </svg>
            <span>Preparing payment details, please wait...</span>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-md shadow-sm mb-4">
            <svg
              className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v2m0 4h.01M21 12c0 4.97-4.03 9-9 9S3 16.97 3 12 7.03 3 12 3s9 4.03 9 9z"
              />
            </svg>
            <div>
              <div className="font-semibold">Payment Error</div>
              <div>
                {error.message || "Something went wrong. Please try again."}
              </div>
            </div>
          </div>
        )}
        {stripeData && (
          <StripePayment
            clientSecret={stripeData.clientSecret}
            intentId={stripeData.intentId}
            amount={price}
            currency={currency}
            onSuccess={(paymentIntent, saveCard) => {
              if (onPaymentSuccess) {
                if (selectedPlan._id) paymentSuccess({
                  orderId,
                  planId: selectedPlan._id,
                  isSaveMyCard: saveCard,
                  paymentMethodId: !!saveCard ? paymentIntent.payment_method : null
                })
                onPaymentSuccess(paymentIntent);
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

export default PaymentPage;
